import { cx } from '@emotion/css';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import PushPinIcon from '@mui/icons-material/PushPin';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';
import React, { memo, useCallback, useMemo, useState } from 'react';
import { ix } from '../../../../../../utils/ix';
import { Button, IconButton } from '../../../../../atoms';
import { InputField } from '../../../../formFields';
import { getCustomViewPermissions } from '../../ViewsMenu.service';
import useStyles from './CustomView.styles';
const COMPONENT_ID = 'custom-view';
export const CustomView = memo(function ({ active, disabled, className, id, maxNameLength, onCancel, onClick, onConfirmationAwait, onDelete, onEdit, onPin, onUnpin, view, }) {
    const classes = useStyles();
    const [actionsPanelVisible, setActionsPanelVisible] = useState(false);
    const [isEditModeActive, setIsEditModeActive] = useState(false);
    const [isDeleteModeActive, setIsDeleteModeActive] = useState(false);
    const [editedName, setEditedName] = useState(view.name);
    const [isEditingAllowed, isOpeningAllowed, isPinningAllowed, isRemovingAllowed] = useMemo(() => {
        const { edit, open, pin, remove } = getCustomViewPermissions(view);
        return [edit, open, pin, remove];
    }, [view]);
    const handleMouseEnter = useCallback(() => {
        if (disabled) {
            return;
        }
        setActionsPanelVisible(true);
    }, [disabled]);
    const handleMouseDown = useCallback(() => {
        if (active || disabled || isDeleteModeActive || isEditModeActive || !isOpeningAllowed)
            return;
        onClick();
    }, [active, disabled, isDeleteModeActive, isEditModeActive, isOpeningAllowed, onClick]);
    const handleMouseLeave = useCallback(() => {
        setActionsPanelVisible(false);
    }, []);
    const handleDeleteClick = useCallback((e) => {
        e.stopPropagation();
        onConfirmationAwait();
        setIsDeleteModeActive(true);
    }, [onConfirmationAwait]);
    const handleDeleteConfirm = useCallback((e) => {
        e.stopPropagation();
        onDelete();
        setIsDeleteModeActive(false);
    }, [onDelete]);
    const handleDeleteCancel = useCallback((e) => {
        e.stopPropagation();
        onCancel();
        setIsDeleteModeActive(false);
    }, [onCancel]);
    const handleEditClick = useCallback((e) => {
        e.stopPropagation();
        onConfirmationAwait();
        setIsEditModeActive(true);
    }, [onConfirmationAwait]);
    const handleEditConfirm = useCallback((e) => {
        e.stopPropagation();
        onEdit(editedName);
        setIsEditModeActive(false);
    }, [editedName, onEdit]);
    const handleEditCancel = useCallback((e) => {
        e.stopPropagation();
        onCancel();
        setEditedName(view.name);
        setIsEditModeActive(false);
    }, [onCancel, view.name]);
    const handleEditInputChange = useCallback((e) => {
        setEditedName(e.target.value);
    }, []);
    const handleEditInputKeyDown = useCallback((e) => {
        e.stopPropagation();
    }, []);
    const handleEditInputClick = useCallback((e) => {
        e.stopPropagation();
    }, []);
    const handlePinClick = useCallback((e) => {
        e.stopPropagation();
        if (view.pinned) {
            onUnpin();
        }
        else {
            onPin();
        }
    }, [onPin, onUnpin, view.pinned]);
    const getId = useCallback((...ids) => ix(id, COMPONENT_ID, ...ids), [id]);
    const renderLeftColumn = () => {
        if (isEditModeActive)
            return null;
        return (React.createElement(React.Fragment, null,
            React.createElement("div", { className: cx(classes.viewName, { active, isEditModeActive }) }, view.name),
            React.createElement("div", { className: classes.viewCreatedBy },
                "Created by ",
                view.createdBy)));
    };
    const renderDeletionPanel = () => (React.createElement("div", { className: classes.deleteContainer, id: getId('delete-view-container') },
        React.createElement(Button, { id: getId('delete-view-cancel-btn'), color: "secondary", onMouseDown: handleDeleteCancel }, "Cancel"),
        React.createElement(Button, { id: getId('delete-view-confirm-btn'), variant: "outlined", color: "error", onMouseDown: handleDeleteConfirm }, "Delete")));
    const renderEditionPanel = () => {
        const editedNameExceedsMaxLength = typeof maxNameLength !== 'undefined' && editedName.length > maxNameLength;
        return (React.createElement("div", { className: classes.editContainer, id: getId('edit-view-container') },
            React.createElement(InputField, { id: getId('edit-view-input'), value: editedName, onChange: handleEditInputChange, onKeyDown: handleEditInputKeyDown, textFieldProps: {
                    variant: 'outlined',
                    onMouseDown: handleEditInputClick,
                }, error: editedNameExceedsMaxLength, helperText: editedNameExceedsMaxLength ? 'Provided name is too long' : undefined }),
            React.createElement(Button, { id: getId('edit-view-cancel-btn'), color: "secondary", onMouseDown: handleEditCancel }, "Cancel"),
            React.createElement(Button, { id: getId('edit-view-confirm-btn'), variant: "outlined", onMouseDown: handleEditConfirm, disabled: !editedName || editedNameExceedsMaxLength }, "Update")));
    };
    const renderActionsPanel = () => (React.createElement("div", { className: classes.panel, id: getId('actions-panel') },
        isEditingAllowed && (React.createElement(IconButton, { id: getId('edit-icon-btn'), className: classes.panelIconButton, onMouseDown: handleEditClick },
            React.createElement(EditIcon, { className: classes.panelIcon }))),
        isRemovingAllowed && (React.createElement(IconButton, { id: getId('delete-icon-btn'), className: classes.panelIconButton, onMouseDown: handleDeleteClick },
            React.createElement(DeleteIcon, { className: classes.panelIcon }))),
        isPinningAllowed ? (React.createElement(IconButton, { id: getId('pin-icon-btn'), className: classes.panelIconButton, onMouseDown: handlePinClick }, view.pinned ? (React.createElement(PushPinOutlinedIcon, { className: classes.panelIcon })) : (React.createElement(PushPinIcon, { className: classes.panelIcon })))) : (view.pinned && React.createElement(PushPinIcon, { id: getId('pinned-icon'), className: classes.pinIcon }))));
    const renderRightColumn = () => {
        const isViewPinned = view.pinned;
        if (isDeleteModeActive) {
            return renderDeletionPanel();
        }
        if (isEditModeActive) {
            return renderEditionPanel();
        }
        if (actionsPanelVisible) {
            return renderActionsPanel();
        }
        if (isViewPinned) {
            return React.createElement(PushPinIcon, { id: getId('pinned-icon'), className: classes.pinIcon });
        }
        return null;
    };
    const disableRipple = active || disabled || isDeleteModeActive || isEditModeActive || !isOpeningAllowed;
    return (React.createElement(Button, { className: cx(className, classes.root, {
            active,
            disabled,
            openingDisallowed: !isOpeningAllowed,
        }), disableRipple: disableRipple, id: id, onMouseDown: handleMouseDown, onMouseEnter: handleMouseEnter, onMouseLeave: handleMouseLeave },
        React.createElement("div", { className: classes.leftColumn, id: getId('left-column') }, renderLeftColumn()),
        React.createElement("div", { className: classes.rightColumn, id: getId('right-column') }, renderRightColumn())));
});
//# sourceMappingURL=CustomView.js.map